/**
 * WebSocket Service for real-time communication
 */

export type MessageType =
  | 'connection_status'
  | 'pong'
  | 'chat'
  | 'task_created'
  | 'task_update'
  | 'task_completed'
  | 'task_failed'
  | 'kill_switch_activated'
  | 'payment_notification'
  | 'subscription_status'
  | 'error'
  // Task progress events (Phase 2)
  | 'task_queue_created'
  | 'task_queue_update'
  | 'task_step_started'
  | 'task_step_progress'
  | 'task_step_completed'
  | 'task_step_failed'
  // Authorization events (Phase 3)
  | 'authorization_required'
  | 'authorization_decision'
  | 'authorization_decision_ack'
  | 'authorization_result'
  | 'pending_authorizations'

export interface WebSocketMessage {
  type: MessageType
  data: Record<string, unknown>
  timestamp: string
}

export type MessageHandler = (message: WebSocketMessage) => void

class WebSocketService {
  private ws: WebSocket | null = null
  private url: string
  private handlers: Map<MessageType, Set<MessageHandler>> = new Map()
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 1000
  private pingInterval: number | null = null
  private isConnecting = false

  constructor() {
    this.url = import.meta.env.VITE_WS_URL || 'ws://localhost:8200/api/v1/ws'
  }

  connect(token?: string): void {
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
      return
    }

    this.isConnecting = true
    const wsUrl = token ? `${this.url}?token=${token}` : this.url

    try {
      this.ws = new WebSocket(wsUrl)

      this.ws.onopen = () => {
        console.log('[WS] Connected')
        this.isConnecting = false
        this.reconnectAttempts = 0
        this.startPingInterval()
      }

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data)
          this.dispatchMessage(message)
        } catch (error) {
          console.error('[WS] Failed to parse message:', error)
        }
      }

      this.ws.onclose = (event) => {
        console.log('[WS] Disconnected:', event.code, event.reason)
        this.isConnecting = false
        this.stopPingInterval()
        this.attemptReconnect(token)
      }

      this.ws.onerror = (error) => {
        console.error('[WS] Error:', error)
        this.isConnecting = false
      }
    } catch (error) {
      console.error('[WS] Connection failed:', error)
      this.isConnecting = false
    }
  }

  disconnect(): void {
    this.stopPingInterval()
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect')
      this.ws = null
    }
    this.reconnectAttempts = this.maxReconnectAttempts // Prevent reconnection
  }

  private attemptReconnect(token?: string): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('[WS] Max reconnection attempts reached')
      return
    }

    this.reconnectAttempts++
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1)

    console.log(`[WS] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`)

    setTimeout(() => {
      this.connect(token)
    }, delay)
  }

  private startPingInterval(): void {
    this.pingInterval = window.setInterval(() => {
      this.send({ type: 'ping', data: {} })
    }, 30000)
  }

  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval)
      this.pingInterval = null
    }
  }

  send(message: { type: string; data: Record<string, unknown> }): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message))
    } else {
      console.warn('[WS] Cannot send message, not connected')
    }
  }

  sendChat(
    content: string,
    conversationId?: string,
    attachmentIds?: string[],
    options?: { isVoiceInput?: boolean; voice?: string }
  ): void {
    this.send({
      type: 'chat',
      data: {
        message: content,
        conversation_id: conversationId,
        file_ids: attachmentIds,
        is_voice_input: options?.isVoiceInput || false,
        voice: options?.voice || 'rachel',
      },
    })
  }

  subscribe(events: string[]): void {
    this.send({
      type: 'subscribe',
      data: { events },
    })
  }

  on(type: MessageType, handler: MessageHandler): () => void {
    if (!this.handlers.has(type)) {
      this.handlers.set(type, new Set())
    }
    this.handlers.get(type)!.add(handler)

    // Return unsubscribe function
    return () => {
      this.handlers.get(type)?.delete(handler)
    }
  }

  off(type: MessageType, handler: MessageHandler): void {
    this.handlers.get(type)?.delete(handler)
  }

  private dispatchMessage(message: WebSocketMessage): void {
    const handlers = this.handlers.get(message.type)
    if (handlers) {
      handlers.forEach((handler) => handler(message))
    }
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }
}

export const wsService = new WebSocketService()
export default wsService
